"use client"

import { useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import {
  LayoutDashboard,
  ClipboardList,
  Cpu,
  CheckSquare,
  BookOpen,
  BarChart3,
  FileText,
  Settings,
  Menu,
  X,
  Brain,
  ChevronDown,
  Bell,
  HelpCircle,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import type { UserRole } from "@/lib/types"

const navItems = [
  { href: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { href: "/submit-pain-point", label: "Submit Pain Point", icon: ClipboardList },
  { href: "/ai-mapping", label: "AI Mapping", icon: Cpu },
  { href: "/feasibility-review", label: "Feasibility Review", icon: CheckSquare },
  { href: "/central-register", label: "Central Register", icon: BookOpen },
  { href: "/priority-matrix", label: "Priority Matrix", icon: BarChart3 },
  { href: "/reports", label: "Reports", icon: FileText },
  { href: "/settings", label: "Settings", icon: Settings },
]

const roles: UserRole[] = [
  "Department User",
  "Department Head",
  "AI Team",
  "SOA Reviewer",
  "Infra Reviewer",
  "Network Reviewer",
  "IT-Ops Reviewer",
  "Admin / Governance User",
]

interface AppShellProps {
  children: React.ReactNode
  pageTitle: string
}

export function AppShell({ children, pageTitle }: AppShellProps) {
  const pathname = usePathname()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [currentRole, setCurrentRole] = useState<UserRole>("AI Team")

  return (
    <div className="flex h-screen bg-muted/30 overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-foreground/20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-card border-r border-border transition-transform duration-200 lg:static lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="flex size-8 items-center justify-center rounded-lg bg-primary">
            <Brain className="size-4 text-primary-foreground" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-foreground leading-tight">AI Use Case</span>
            <span className="text-xs text-muted-foreground leading-tight">Register</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto lg:hidden size-7"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="flex flex-col gap-1">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = pathname === item.href
              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    <Icon className="size-4 shrink-0" />
                    {item.label}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        {/* User */}
        <div className="border-t border-border px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
              JD
            </div>
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-medium truncate">John Doe</span>
              <span className="text-xs text-muted-foreground truncate">{currentRole}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <header className="flex h-14 items-center gap-4 border-b border-border bg-card px-4 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden size-8"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="size-4" />
          </Button>

          <h1 className="text-sm font-semibold text-foreground flex-1">{pageTitle}</h1>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="size-8">
              <Bell className="size-4" />
            </Button>
            <Button variant="ghost" size="icon" className="size-8">
              <HelpCircle className="size-4" />
            </Button>

            {/* Role switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 h-8 text-xs font-medium shadow-sm hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-colors">
                <span className="hidden sm:inline">{currentRole}</span>
                <span className="sm:hidden">Role</span>
                <ChevronDown className="size-3" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {roles.map((role) => (
                  <DropdownMenuItem
                    key={role}
                    onClick={() => setCurrentRole(role)}
                    className={cn("text-xs", currentRole === role && "font-semibold")}
                  >
                    {role}
                    {currentRole === role && (
                      <Badge variant="secondary" className="ml-auto text-xs py-0">Active</Badge>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  )
}
